/**
 * i18n 核心 — locale 型別、persistence、translate()。
 *
 * - 支援語言：zh-TW（來源）/ en / ja，對應 PRD 三語需求
 * - locale 持久化於 localStorage（key `boat.pwa.locale`）
 * - `translate()` 為純函式；React 端由 `i18n-context.tsx` 之 `useI18n()` 包裝
 * - `getStoredLocale()` 供非 React 程式（`api-client` 之 `Accept-Language`）取用
 */
import { en } from './locales/en';
import { ja } from './locales/ja';
import { zhTW, type TranslationKey } from './locales/zh-TW';

export type Locale = 'zh-TW' | 'en' | 'ja';

export const LOCALES: readonly Locale[] = ['zh-TW', 'en', 'ja'];

/** 語言切換 UI 顯示名（各以該語言自稱）。 */
export const LOCALE_LABELS: Record<Locale, string> = {
  'zh-TW': '繁體中文',
  en: 'English',
  ja: '日本語',
};

export const DEFAULT_LOCALE: Locale = 'zh-TW';

const CATALOGS: Record<Locale, Record<TranslationKey, string>> = {
  'zh-TW': zhTW,
  en,
  ja,
};

const STORAGE_KEY = 'boat.pwa.locale';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** 讀目前 locale；localStorage 不可用或無值 → DEFAULT_LOCALE。 */
export function getStoredLocale(): Locale {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (isLocale(raw)) return raw;
  } catch {
    // localStorage 不可用（隱私模式 / SSR）— 落 default
  }
  return DEFAULT_LOCALE;
}

export function storeLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // 寫入失敗忽略；下次仍落 default
  }
}

/**
 * 取 key 對應字串並做 `{name}` 插值。
 *
 * - 目標 locale 缺 key → fallback 至 zh-TW；再缺 → 回 key 本身（避免炸畫面）
 * - params 未提供之佔位保留 `{name}` 原樣（方便發現漏帶）
 */
export function translate(
  locale: Locale,
  key: TranslationKey,
  params?: Record<string, string | number>,
): string {
  const catalog = CATALOGS[locale] ?? CATALOGS[DEFAULT_LOCALE];
  const template = catalog[key] ?? CATALOGS[DEFAULT_LOCALE][key] ?? key;
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_match, name: string) =>
    params[name] !== undefined ? String(params[name]) : `{${name}}`,
  );
}

export type { TranslationKey };
